import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { RuntimeConfigService } from './runtime-config.service';
import { getApiUrl } from '../config/config-helpers';

/** An announcement / patch-note entry. `bodyMarkdown` is raw Markdown — render it sanitized, never as trusted HTML. */
export interface Announcement {
  id: string;
  title: string;
  bodyMarkdown: string;
  isPublished: boolean;
  createdAt: string;
  updatedAt: string;
  publishedAt?: string | null;
}

/** Payload for creating an announcement (admin only). */
export interface CreateAnnouncementRequest {
  title: string;
  bodyMarkdown: string;
  isPublished: boolean;
}

/** Payload for updating an announcement (admin only). */
export interface UpdateAnnouncementRequest {
  title: string;
  bodyMarkdown: string;
  isPublished: boolean;
}

/**
 * Reads the public patch-notes feed and (for admins) manages announcement CRUD (#747). The admin calls
 * target endpoints gated server-side by the AdministratorOnly policy; a non-admin caller receives 403
 * regardless of what the UI allows.
 */
@Injectable({
  providedIn: 'root'
})
export class AnnouncementService {
  private http = inject(HttpClient);
  private runtimeConfig = inject(RuntimeConfigService);

  private get apiUrl(): string {
    return getApiUrl(this.runtimeConfig);
  }

  /** Most recent published announcements, newest first. Public (anonymous allowed). */
  getRecent(limit = 5): Observable<Announcement[]> {
    return this.http.get<Announcement[]>(`${this.apiUrl}/announcements/recent?limit=${limit}`);
  }

  /** Every announcement (published and draft), newest first. Admin only. */
  getAll(): Observable<Announcement[]> {
    return this.http.get<Announcement[]>(`${this.apiUrl}/admin/announcements`);
  }

  /** Create a new announcement. Admin only. */
  create(request: CreateAnnouncementRequest): Observable<Announcement> {
    return this.http.post<Announcement>(`${this.apiUrl}/admin/announcements`, request);
  }

  /** Update an existing announcement. Admin only. */
  update(id: string, request: UpdateAnnouncementRequest): Observable<Announcement> {
    return this.http.put<Announcement>(`${this.apiUrl}/admin/announcements/${id}`, request);
  }

  /** Delete an announcement. Admin only. */
  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/admin/announcements/${id}`);
  }
}
